import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import type { ScopeState } from '../components/filters/types';
import { DEFAULT_KPI } from '../components/media/data/constants';
import { scopeFromSearch, scopeSearchShape, serializeScope } from './scope-search';

export const REPORTS_ROUTE = '/reports/performance';

export const reportsSearchSchema = z
  .object({
    ...scopeSearchShape,
    kpi: z.string().optional().catch(undefined),
  })
  .catch({});

export type ReportsSearch = z.infer<typeof reportsSearchSchema>;

export function validateReportsSearch(search: Record<string, unknown>): ReportsSearch {
  const parsed = reportsSearchSchema.parse(search);
  const result: ReportsSearch = {};
  if (parsed.brand !== undefined) result.brand = parsed.brand;
  if (parsed.platform !== undefined) result.platform = parsed.platform;
  if (parsed.account !== undefined) result.account = parsed.account;
  if (parsed.creativeType !== undefined) result.creativeType = parsed.creativeType;
  if (parsed.range !== undefined) result.range = parsed.range;
  if (parsed.kpi !== undefined) result.kpi = parsed.kpi;
  return result;
}

export function useReportsSearch() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ReportsSearch;

  const apply = (next: { scope?: ScopeState; kpi?: string }): void => {
    void navigate({
      to: REPORTS_ROUTE,
      replace: true,
      search: (previous) => {
        const merged: Record<string, unknown> = { ...(previous as Record<string, unknown>) };
        if (next.scope) {
          const serialized = serializeScope(next.scope);
          for (const key of Object.keys(scopeSearchShape)) {
            const value = serialized?.[key as keyof typeof scopeSearchShape];
            if (value !== undefined) merged[key] = value;
            else delete merged[key];
          }
        }
        if (next.kpi !== undefined) merged['kpi'] = next.kpi;
        return merged;
      },
    });
  };

  return {
    scope: scopeFromSearch(search),
    hasScope: [search.brand, search.platform, search.account].some(
      (value) => value !== undefined && value.length > 0,
    ),
    kpi: search.kpi ?? DEFAULT_KPI,
    setScope: (scope: ScopeState) => apply({ scope }),
    setKpi: (kpi: string) => apply({ kpi }),
  };
}
